import { Box, Button, Grid, MenuItem, Stack, Typography } from '@mui/material'
import { RootState } from '../store'
import { useDispatch, useSelector } from 'react-redux'
import { User } from '../models/Organization'
import React, { useEffect, useRef, useState } from 'react'
import CustomDropdown from './CustomDropdown'
import { Can } from '../utils/AbilityUtil'
import {
  architectureRoles,
  AttributeActionNames,
  AttributeResourceNames,
  engineeringRoles,
  UserRole,
  UserRoleNames,
} from '../models/AccessControl'
import { updateUserAsync } from '../services/usersService'
import { createSelector } from '@reduxjs/toolkit'
import InviteUserComponent from './InviteUserComponent'

const userSelector = createSelector(
  [(state: RootState) => state.auth],
  (auth) => auth.user,
)
const userRolesSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.userRoles,
)
const userColorsSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.userColors,
)

interface UsersListProps {
  users: User[]
  selectedUserId: number | null
  hanldeUserClick: (userId: number) => void
}

const UsersListComponent: React.FC<UsersListProps> = ({
  users,
  selectedUserId,
  hanldeUserClick,
}) => {
  const dispatch = useDispatch<any>()
  const boxRef = useRef<HTMLDivElement>(null)
  const gridRef = useRef<HTMLDivElement>(null)
  const [boxHeight, setBoxHeight] = useState(0)
  const [gridHeight, setGridHeight] = useState(0)
  const [isInviteUserOpen, setIsInviteUserOpen] = useState(false)
  const user = useSelector(userSelector)
  const userRoles = useSelector(userRolesSelector) ?? []
  const usersColors = useSelector(userColorsSelector) ?? []

  const filteredUserRoles = !!user
    ? architectureRoles.includes(user.role.name)
      ? userRoles.filter(
          (userRole) =>
            architectureRoles.includes(userRole.name) ||
            userRole.name === UserRoleNames.Viewer,
        )
      : engineeringRoles.includes(user.role.name)
        ? userRoles.filter(
            (userRole) =>
              engineeringRoles.includes(userRole.name) ||
              userRole.name === UserRoleNames.Viewer,
          )
        : userRoles
    : userRoles

  const handleInviteUserClick = (showDialog: boolean) => {
    setIsInviteUserOpen(showDialog)
  }

  const handleUserRoleClick = (user: User, userRole: UserRole) => {
    dispatch(
      updateUserAsync({ userId: user.id, info: { role_id: userRole.id } }),
    )
  }

  const handleUserRowClick = (userId: number) => {
    hanldeUserClick(userId)
  }

  useEffect(() => {
    const calculateHeight = () => {
      if (boxRef.current) {
        setBoxHeight(boxRef.current.clientHeight)
      }
      if (gridRef.current) {
        setGridHeight(gridRef.current.clientHeight)
      }
    }

    calculateHeight()
    window.addEventListener('resize', calculateHeight)

    return () => {
      window.removeEventListener('resize', calculateHeight)
    }
  }, [])

  return (
    <Can I={AttributeActionNames.Read} a={AttributeResourceNames.Users}>
      <Stack
        gap={'1rem'}
        sx={{
          height: '100%',
        }}
      >
        <Box
          ref={boxRef}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.94rem',
              fontWeight: 500,
              lineHeight: 'normal',
              color: '#06004A',
            }}
          >
            Member list
          </Typography>
          <Can I={AttributeActionNames.Create} a={AttributeResourceNames.Users}>
            {(allowed) => (
              <Button
                disabled={!allowed}
                size='small'
                type='submit'
                onClick={() => handleInviteUserClick(true)}
                sx={{
                  background: '#1705E5',
                  color: '#fff',
                  textTransform: 'capitalize',
                  fontSize: '0.76rem',
                  minWidth: '6rem',
                }}
              >
                Invite
              </Button>
            )}
          </Can>
        </Box>
        <InviteUserComponent
          isOpen={isInviteUserOpen}
          handleClose={() => handleInviteUserClick(false)}
        />
        <Can I={AttributeActionNames.Update} a={AttributeResourceNames.Users}>
          {(allowed) => (
            <Box
              sx={{
                background: '#FBFBFB',
                borderRadius: '0.6rem',
                boxShadow: '0px 3px 5px 0px #2F2F2F1A;',
                height: `calc(100% - ${boxHeight}px - 1rem)`,
                pb: '1rem',
              }}
            >
              <Grid
                container
                sx={{
                  width: '100%',
                  height: '100%',
                }}
              >
                <Grid
                  ref={gridRef}
                  container
                  sx={{
                    width: '100%',
                    p: '1rem',
                    borderBottom: 1,
                    borderColor: 'divider',
                    mb: '1rem',
                  }}
                >
                  <Grid size={8}>
                    <Typography
                      sx={{
                        fontSize: '0.76rem',
                        lineHeight: 'normal',
                        color: '#878787',
                      }}
                    >
                      Name
                    </Typography>
                  </Grid>
                  <Grid size={4}>
                    <Typography
                      sx={{
                        fontSize: '0.76rem',
                        lineHeight: 'normal',
                        color: '#878787',
                      }}
                    >
                      Role
                    </Typography>
                  </Grid>
                </Grid>
                <Stack
                  sx={{
                    display: 'flex',
                    boxSizing: 'border-box',
                    overflowY: 'auto',
                    width: '100%',
                    height: `calc(100% - ${gridHeight}px)`,
                  }}
                >
                  {!!users &&
                    users.map((user, index) => (
                      <Box
                        key={index}
                        onClick={() => handleUserRowClick(user.id)}
                        sx={{
                          display: 'flex',
                          cursor: 'pointer',
                          borderBottom: 1,
                          borderColor: 'divider',
                          alignItems: 'center',
                          mx: '1rem',
                          py: '0.75rem',
                          px: 0,
                          textTransform: 'none',
                          borderRadius: 0,
                          background:
                            selectedUserId === user.id
                              ? '#E5F0FF'
                              : 'transparent',
                          '&:hover': {
                            background: '#E5F0FF',
                          },
                        }}
                      >
                        <Grid size={8}>
                          <Box
                            gap={'1rem'}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width='32'
                              height='32'
                              viewBox='0 0 32 32'
                              fill='none'
                            >
                              <circle
                                cx='16'
                                cy='16'
                                r='16'
                                fill={
                                  usersColors?.find((usr) => usr.id === user.id)
                                    ?.color ?? '#04002E'
                                }
                              />
                              <text
                                x='50%'
                                y='50%'
                                textAnchor='middle'
                                dy='.3em'
                                fontSize='14'
                                fill='#fff'
                                fontWeight='bold'
                              >
                                {user.user_name
                                  .split(' ')
                                  .map((item) => item.charAt(0))
                                  .join('')
                                  .toUpperCase()}
                              </text>
                            </svg>
                            <Typography
                              sx={{
                                fontSize: '0.82rem',
                                fontWeight: 500,
                                lineHeight: 'normal',
                                color: '#797979',
                              }}
                            >
                              {user.user_name}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid size={4}>
                          <CustomDropdown
                            disabled={!allowed}
                            title={user.role?.name ?? 'Select role'}
                            color='#04002E'
                            buttonStyleProps={{
                              justifyContent: 'flex-start',
                              padding: 0,
                              background: 'transparent',
                              textTransform: 'capitalize',
                            }}
                            titleStyleProps={{
                              fontSize: '14px',
                              fontWeight: 500,
                              textTransform: 'capitalize',
                            }}
                          >
                            {filteredUserRoles.map((item, index) => (
                              <MenuItem
                                key={index}
                                onClick={() => handleUserRoleClick(user, item)}
                                sx={{
                                  color: '#04002E',
                                  '&:hover': {
                                    backgroundColor: '#1705E5',
                                    color: '#fff',
                                  },
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: '12px',
                                    textTransform: 'capitalize',
                                  }}
                                >
                                  {item.name.toLowerCase()}
                                </Typography>
                              </MenuItem>
                            ))}
                          </CustomDropdown>
                        </Grid>
                      </Box>
                    ))}
                </Stack>
              </Grid>
            </Box>
          )}
        </Can>
      </Stack>
    </Can>
  )
}

export default UsersListComponent
